import React, { useState, useEffect, useContext } from "react";
import { useStaticQuery, graphql } from "gatsby";
import tw from "twin.macro";
import Hero from "../hero";
import Input from "./input";
import Select from "./select";
import DocumentLibrary from "./document-library";
import ResourcesContext from "./context";
import Pager from "./pager";
import DocCount from "./doc-count"

import bgSrc from "../../images/filters-background.jpg";

import Tabs from "../tabs";

const Container = tw.div`container mx-4 w-auto sm:mx-auto`;
const FiltersContainer = tw.div`bg-cover bg-no-repeat bg-left py-8 bg-blue-900`;
const DetailsRow = tw.div`mt-8 flex items-center justify-between`;
const Form = tw.div`md:w-7/12 lg:w-5/12 space-y-6`;
const SubHeading = tw.h2`text-2xl text-white font-bold mb-6`;
const FormRow = tw.div`grid grid-cols-1 md:grid-cols-2 gap-6`;
const Button = tw.button`text-white py-2 px-3 text-sm bg-blue-900 hover:bg-blue-700 focus:bg-blue-700 rounded transition`;

const ITEMS_PER_PAGE = 10;

const tabs = [
  { label: "Program Resources", active: false, to: "/program-resources" },
  {
    label: "LAO Resources",
    active: true,
    to: "/program-resources/lao-resources",
  },
];

const ResourcesContent = ({ params }) => {
  const title = params.get("title");
  const category = params.get("category");
  const type = params.get("type");
  const lao = params.get("lao");

  const data = useStaticQuery(graphql`
    query {
      laos: allLaosJson(
        sort: { fields: [sort], order: ASC }
        filter: { type: { eq: "lao" } }
      ) {
        edges {
          node {
            id
            name
            abbrev
          }
        }
      }
    }
  `);

  const resourcesContext = useContext(ResourcesContext);
  const initialFilters = {
    title: title || "",
    category: category || "",
    type: type || "",
    lao: lao || "",
  };
  const {
    loading,
    error,
    types,
    categories,
    laos,
    docs,
    getAoResources,
  } = resourcesContext;
  const [page, setPage] = useState(1);
  const [documents, setDocuments] = useState([]);
  const [filters, _setFilters] = useState(initialFilters);

  const numPages = Math.ceil(documents.length / ITEMS_PER_PAGE);
  const hasFilters = filters.title || filters.category || filters.type;

  /**
   * Update filters; do not use _setFilter manual
   * @param {string} name - key in filters to modify
   * @param {string} value - new value for the filter
   */
  const filter = (name, value) => {
    // set URL parameter for persistence
    const url = new URL(location);
    //params.forEach((v, k) => url.searchParams.set(k,v))
    if (filters.title) url.searchParams.set("title", filters.title);
    if (filters.category) url.searchParams.set("category", filters.category);
    if (filters.type) url.searchParams.set("type", filters.type);
    if (filters.lao) url.searchParams.set("lao", filters.lao);
    url.searchParams.set(name, value);
    history.pushState({}, "", url);

    // filter state data
    _setFilters({ ...filters, [name]: value });
  };

  /**
   * Checks if a doc matches a filter
   * @param filter {string} name of the key in the filters state object
   * @param doc {object} document to check
   * @returns {bool} true if value of filter found in doc
   */
  const checkFilter = (filter, doc) => {
    const value = filters[filter];
    const property = doc[filter];
    if (!value) return true;
    if (property == undefined) return false;
    if (filter == "title") {
      return (
        property.toLowerCase().includes(value.toLowerCase()) ||
        doc?.keywords?.some((item) =>
          item.toLowerCase().includes(value.toLowerCase())
        )
      );
    }
    return Array.isArray(property)
      ? property.some((item) =>
          item.toLowerCase().includes(value.toLowerCase())
        )
      : property.toLowerCase().includes(value.toLowerCase());
  };

  /**
   * Clear all filters and reset page to default state
   */
  const clearFilters = () => {
    _setFilters({ title: "", category: "", type: "", lao: "" });
    // clear URL params so they don't persist if the user reloads the page
    const url = new URL(location);
    url.searchParams.forEach((v, k) => url.searchParams.delete(k));
    history.pushState({}, "", url.origin + url.pathname);
    // Reset to first page
    changePage(1);
  };

  /**
   * Get the index of the first resource that is currently displayed (e.g. first item on page 2)
   * @returns index of first resource currently displayed
   */
  const first = () => page * ITEMS_PER_PAGE - ITEMS_PER_PAGE;

  /**
   * Get the index of the last resource that is currently displayed
   * @returns index of last resource currently displayed;
   */
  const last = () => {
    const l = page * ITEMS_PER_PAGE;
    return l > documents.length ? documents.length : l;
  };

  /**
   * Change page and scroll to the top of the resources
   * @param n Target page number
   */
  const changePage = (n) => {
    if (n < 1 || numPages) setPage(1);
    else if (n >= numPages) setPage(numPages);
    else setPage(n);
    const el = document.querySelector("#top");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    setDocuments(
      docs
        .filter((doc) => {
          for (let filter in filters) {
            // looking false from checkFilter (e.g. document doesn't meet a
            // current filter) to determine if a document should be excluded,
            // otherwise include it. You can't just return the result of
            // checkFilter or you'll include documents that only meet some
            // criteria (e.g. true for title but not category... this would have
            // already returned true before the category was checked)
            if (!checkFilter(filter, doc)) return false;
          }
          return true;
        })
        .map((doc) => {
          const edges = data.laos.edges.filter(
            (edge) => edge.node.id == doc.lao
          );
          if (edges.length == 0) return doc;
          const node = edges[0].node;
          return {
            ...doc,
            lao: node.abbrev,
          };
        })
    );
    // eslint complains about checkFilter not being a dependency because it is accessing filters state directly
    /* eslint-disable-next-line */
  }, [filters, docs]);

  useEffect(
    () => {
      getAoResources();
    },
    /* eslint-disable-next-line */
    []
  );

  return (
    <>
      <Hero>Resources</Hero>
      <div tw="bg-gray-100">
        <div tw="container mx-4 sm:mx-auto">
          <Tabs tabs={tabs} />
        </div>
      </div>
      {loading ? (
        <Container tw="my-8">Loading</Container>
      ) : error ? (
        error
      ) : (
        <>
          <FiltersContainer
            style={{
              backgroundImage: `linear-gradient(to right, #004c83 50%, #004c8300), url(${bgSrc})`,
            }}
          >
            <Container>
              <SubHeading>
                Lead Academic Organization (LAO) Resources
              </SubHeading>
              <Form>
                <FormRow>
                  <Input
                    value={filters.title}
                    label="Title / Keyword"
                    id="filter-title"
                    name="title"
                    onChange={(e) => filter(e.target.name, e.target.value)}
                  />
                  <Select
                    value={filters.lao}
                    label="LAO"
                    id="filter-lao"
                    name="lao"
                    onChange={(e) => filter(e.target.name, e.target.value)}
                  >
                    <option value=""></option>
                    {laos.map((lao, i) => {
                      const edges = data.laos.edges.filter(
                        (edge) => edge.node.id == lao
                      );
                      if (edges.length == 0) return;
                      const node = edges[0].node;
                      return (
                        <option key={`lao-${i}`} value={lao}>
                          {`${node.name} (${node.abbrev})`}
                        </option>
                      );
                    })}
                  </Select>
                </FormRow>
                <FormRow>
                  <Select
                    value={filters.category}
                    label="Category"
                    id="filter-category"
                    name="category"
                    onChange={(e) => filter(e.target.name, e.target.value)}
                  >
                    <option value=""></option>
                    {categories.map((category, i) => (
                      <option key={`category-${i}`} value={category}>
                        {category}
                      </option>
                    ))}
                  </Select>
                  <Select
                    value={filters.type}
                    label="Document Type"
                    id="filter-type"
                    name="type"
                    onChange={(e) => filter(e.target.name, e.target.value)}
                  >
                    <option value=""></option>
                    {types.map((type, i) => (
                      <option key={`type-${i}`} value={type}>
                        {type}
                      </option>
                    ))}
                  </Select>
                </FormRow>
                <div>
                  <Button type="button" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                </div>
              </Form>
            </Container>
          </FiltersContainer>
          <Container>
            <>
              <DetailsRow>
                <div></div>
                    <DocCount first={first()} last={last()} total={documents.length}/>
              </DetailsRow>

              <DocumentLibrary
                includeLao
                documents={documents.slice(first(), last())}
              />
              <Pager
                page={page}
                numPages={numPages}
                pageChangeCb={changePage}
              />
            </>
          </Container>
        </>
      )}
    </>
  );
};

export default ResourcesContent;
